// src/components/survey/CreateSurveyDialog.js

import React, { useState } from 'react';
import { LoaderCircle, FileText, FileChartColumn } from 'lucide-react'; // For loading spinner

// Import your reusable UI components
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '../ui/Dialog';
import Button from '../ui/Button';


// Import your authenticated API hook
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import API_CONFIG from '../../config/api'

const BASE_URL = API_CONFIG.BASE_URL

/**
 * A dialog component for creating a new survey.
 * It handles its own form state, API calls, loading, and errors.
 *
 * @param {object} props
 * @param {function(object): void} props.onSurveyCreated - A callback function that is triggered
 * with the new survey data upon successful creation.
 */
export default function CreateSurveyDialog({ onSurveyCreated }) {
	// State to control the dialog's open/closed status
	const [isOpen, setIsOpen] = useState(false);

	// State for the form input
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [briefSelectedFile, setBriefSelectedFile] = useState(null);
	const [dataMapSelectedFile, setDataMapSelectedFile] = useState(null);
	const [primaryLanguage, setPrimaryLanguage] = useState('FR');
	const [useAI, setUseAI] = useState(true)
	
	const languages = ['FR', 'EN', 'CH']

	// State for API call status
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// Get the authenticated 'post' method from your hook
	const { authenticatedFetch } = useAuthenticatedApi();

	const handleFileChange = (e, fileType) => {
		const file = e.target.files[0];
		if (file) {
			// Validate file type
			// TODO add some file verification / validation (ex: file.endwith(".csv"))
			if (fileType === 'brief') {
				setBriefSelectedFile(file);
			} else if (fileType === 'dataMap') {
				setDataMapSelectedFile(file);
			}
			setError(null);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!title.trim()) {
			setError('Title cannot be empty.');
			return;
		}
		if (!description.trim()) {
			setError('Description cannot be empty.');
			return;
		}
		if (!primaryLanguage.trim()) {
			setError('Primary language cannot be empty.');
			return;
		}
		setIsLoading(true);
		setError(null);

		try {
			const formData = new FormData();
			formData.append('survey_title', title);
			formData.append('survey_seed_description', description);
			formData.append('primary_language', primaryLanguage)
			formData.append('use_ai_generation', useAI)
			// Append the files (only if they exist)
			if (briefSelectedFile) {
				formData.append('brief_file', briefSelectedFile);
			}
			if (dataMapSelectedFile) {
				formData.append('datamap_file', dataMapSelectedFile);
			}

			// Submit with FormData
			const material = {
				body: formData, // Don't stringify FormData
			};
			const newSurvey = await authenticatedFetch(BASE_URL + '/surveys/create-new-survey', material, 'POST');
			// Call the callback function passed from the parent component
			onSurveyCreated(newSurvey);

			// Reset state and close the dialog on success
			setUseAI(true)
			setTitle('');
			setDescription('');
			setIsOpen(false);
			setBriefSelectedFile(null);
			setDataMapSelectedFile(null);
		} catch (err) {
			setError(err.message || 'Failed to create survey. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};
	
	// When the dialog's open state changes, reset the error message
	const handleOpenChange = (open) => {
		if (!open) {
			setUseAI(true);
			setTitle('');
			setDescription('');
			setError(null);
			setBriefSelectedFile(null);
			setDataMapSelectedFile(null);
		}
		setIsOpen(open);
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant="primary" >+ New survey</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[750px]">
				<DialogHeader>
					<DialogTitle className="text-primary">Create a New Survey</DialogTitle>
					<DialogDescription>
						Give your new survey a title and a seed description. Our AI will use it to get started.
					</DialogDescription>
				</DialogHeader>
	
				<form onSubmit={handleSubmit} className="py-4">
					<div className="grid gap-6">
						{/* Title and Language on same row */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="grid gap-2">
								<label htmlFor="title" className="font-semibold text-primary">
									Title
								</label>
								<input
									id="title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									placeholder="e.g., Impact of Remote Work on Employee Well-being"
									className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
									disabled={isLoading}
								/>
							</div>
							
							<div className="grid grid-cols-2 gap-4">
								<div className="grid gap-2">
									<label htmlFor="primaryLanguage" className="font-semibold text-primary">
										Primary Language
									</label>
									<select
										id="primaryLanguage"
										value={primaryLanguage}
										onChange={(e) => setPrimaryLanguage(e.target.value)}
										className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
										disabled={isLoading}
									>
										{languages.map((language) => (
											<option key={language} value={language}>
												{language}
											</option>
										))}
									</select>
								</div>

								<div className="grid gap-2">
									<label htmlFor="useAI" className="font-semibold text-primary">
										Use AI generation
									</label>
									<div className="flex items-center h-[38px]">
										<label className="relative inline-flex items-center cursor-pointer">
											<input
												id="useAI"
												type="checkbox"
												checked={useAI}
												onChange={(e) => setUseAI(e.target.checked)}
												className="sr-only"
												disabled={isLoading}
											/>
											<div className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-ring rounded-full peer transition-colors ${useAI ? 'bg-primary' : 'bg-gray-200'}`}>
												<div className={`absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform ${useAI ? 'translate-x-full border-white' : ''}`}></div>
											</div>
										</label>
									</div>
								</div>
							</div>
						</div>
	
						{/* Description - full width */}
						<div className="grid gap-2">
							<label htmlFor="description" className="font-semibold text-primary">
								Seed Description
							</label>
							<textarea
								id="description"
								rows={5}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Describe the main goal of your survey. For example: 'I want to understand how the shift to full-time remote work since 2020 has affected the mental and physical health of tech employees...'"
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
						</div>
	
						{/* Brief File - full width */}
						<div className="grid gap-2">
							<label htmlFor="briefFile" className="font-semibold text-primary">
								Import your <strong>survey brief</strong> 
							</label>
							<input
								id="briefFile"
								type="file"
								onChange={(e) => handleFileChange(e, 'brief')} 
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
							{briefSelectedFile && (
								<p className="text-sm text-gray-600 flex items-center gap-2">
									<FileText className="h-4 w-4" />
									Selected: {briefSelectedFile.name}
								</p>
							)}
						</div>
	
						{/* Data Map File - full width */}
						<div className="grid gap-2">
							<label htmlFor="dataMapFile" className="font-semibold text-primary">
								Import your <strong>data map</strong> 
							</label>
							<input
								id="dataMapFile"
								type="file"
								onChange={(e) => handleFileChange(e, 'dataMap')} 
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
							{dataMapSelectedFile && (
								<p className="text-sm text-gray-600 flex items-center gap-2">
									<FileChartColumn className="h-4 w-4" />
									Selected: {dataMapSelectedFile.name}
								</p>
							)}
						</div>
						
						{error && <p className="text-center text-sm text-red-500">{error}</p>}
					</div>
	
					<DialogFooter className="pt-8">
						<Button type="submit" disabled={isLoading || !title.trim()}>
							{isLoading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
							Create Survey
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}